import React from "react";
import { motion } from "framer-motion";

// state: "idle" | "listening" | "thinking" | "speaking"
const STATE_META = {
  idle:      { color: "#FFFFFF", glow: "0 0 45px rgba(235,235,235,0.18)", label: "Ready" },
  listening: { color: "#FFFFFF", glow: "0 0 75px rgba(235,235,235,0.5)",  label: "Listening" },
  thinking:  { color: "#E4E4E4", glow: "0 0 60px rgba(235,235,235,0.4)",  label: "Thinking" },
  speaking:  { color: "#E6E6E6", glow: "0 0 85px rgba(228,228,228,0.5)", label: "Speaking" },
};

// Static animation configs (kept out of render to avoid new refs each frame).
const CORE_SCALE = {
  idle:      { scale: [1, 1.04, 1] },
  listening: { scale: [1, 1.06, 1] },
  thinking:  { scale: [0.96, 1, 0.96] },
  speaking:  { scale: [1, 1.03, 1] },
};
const RING_TRANSITION = { duration: 6, repeat: Infinity, ease: "linear" };
const SWIRL_ANIM = { rotate: 360 };

export function Orb({ state = "idle", size = 260, accent }) {
  const meta = STATE_META[state] || STATE_META.idle;
  const ringColor = accent || meta.color;

  return (
    <div className="orb-wrap" style={{ width: size, height: size }} data-testid="ai-orb-container" data-state={state}>
      {/* outer ambient glow rings */}
      {state === "listening" && [0, 0.6, 1.2].map((d) => (
        <span key={d} className="ripple" style={{ width: size * 0.9, height: size * 0.9, borderColor: `${ringColor}66`, animationDelay: `${d}s` }} />
      ))}
      {state === "speaking" && [0, 0.5].map((d) => (
        <span key={d} className="ripple" style={{ width: size * 0.85, height: size * 0.85, borderColor: "rgba(230,230,230,0.4)", animationDelay: `${d}s` }} />
      ))}

      {/* rotating particle ring for thinking */}
      {state === "thinking" && (
        <motion.div
          className="orb-ring"
          style={{ width: size * 0.98, height: size * 0.98, borderStyle: "dashed", borderColor: `${ringColor}55`, borderWidth: 1 }}
          animate={SWIRL_ANIM}
          transition={RING_TRANSITION}
        />
      )}

      <motion.div
        className="orb-core"
        style={{ width: size * 0.62, height: size * 0.62, boxShadow: `inset 0 0 40px rgba(235,235,235,0.35), ${meta.glow}` }}
        animate={CORE_SCALE[state] || CORE_SCALE.idle}
        transition={{ duration: state === "listening" ? 1.1 : 3.6, repeat: Infinity, ease: "easeInOut" }}
      >
        {/* inner swirl */}
        <motion.div
          style={{
            position: "absolute", inset: "18%", borderRadius: "9999px",
            background: "conic-gradient(from 0deg, rgba(235,235,235,0.0), rgba(235,235,235,0.5), rgba(235,235,235,0.0))",
            filter: "blur(6px)",
          }}
          animate={SWIRL_ANIM}
          transition={{ duration: state === "thinking" ? 3 : 10, repeat: Infinity, ease: "linear" }}
        />

        {/* equalizer for speaking / listening */}
        {(state === "speaking" || state === "listening") && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 4, height: "100%" }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="eq-bar" style={{ height: "40%", animationDelay: `${i * 0.12}s`, opacity: 0.85 }} />
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}

export const orbLabel = (s) => (STATE_META[s] || STATE_META.idle).label;
